import React from "react";
import { track, membershipLevel, imageFailure } from "../../helpers/analytics";
import ChatHistory from "../../components/chat-history";
import InputBox from "../../components/inputBox";
import withRouter from "../../helpers/withRouter";
import {
  getConversationsByConversationId,
  postChatForCharacter,
  postImageForCharacter,
  waitForImageGenerationJob,
} from "../../api/rest-apis";
import { Character } from "../../interfaces/rest-api";
import { ImageChatRequest } from "../../interfaces/rest-api";
import Popup from "../../components/popup";
import { reloadPage } from "../../helpers/reload";

import { delay } from "../../helpers/functions";
import PromptTabMobile from "../../components/prompt-tab-mobile";
import { emitter } from "./context/event";
import { isEqual } from "lodash";
import SettingsModal from "../../components/ReaderModeSettings";
import { Link } from "react-router-dom";

interface ChatProps {
  id: string;
  c: string;
  action: string;
  updateParams: any;
  higherLevelProps: any;
  character: Character | any;
  newChat: boolean;
  resetChatFunc: any;
  isVoiceEnabled: boolean;
  updateConversationId: any;
  saved: boolean;
  save: any;
  showHistory: any;
  updateIsVoiceEnabled: any;
  userPlan: string;
  popUpReplace: string;
  haveAnyPrompt: boolean;
  bookTalk: any;
  book_talk_prompts: string[];
}

interface Message {
  message: string;
  message_type?: "text" | "image";
  media_url?: string;
  media_alt?: string;
  right: boolean;
}

interface ChatState {
  selectedSize: 14 | 16 | 18 | 20;
  selectedFont: "lexend" | "opendyslexia";
  showSettings: boolean;
  history: Message[];
  inputField: string;
  loadingNewChat: boolean;
  conversation_id: string;
  showBothVoiceText: boolean;
  plan: string;
  popUp: boolean;
  reasonForPopUp: string;
  popUpReplace: string;
  newChatReceived: boolean;
  bookTalk: boolean;
  streaming: boolean;
  readerMode: boolean;
  currentBookmarkIndex: number;
  isModalOpen: boolean;
  requiredTier: string;
  isImageFeature: boolean;
  imageNotice: string;
}

class Chat extends React.Component<ChatProps, ChatState> {
  chatHistoryRef!: React.RefObject<HTMLDivElement>;
  private observer: IntersectionObserver | null = null;
  private scrollTimeout: NodeJS.Timeout | null = null;

  constructor(props: ChatProps) {
    super(props);
    this.chatHistoryRef = React.createRef();

    const savedFont =
      (localStorage.getItem("readerFont") as "lexend" | "opendyslexia") ||
      "lexend";
    const savedSize = parseInt(
      (localStorage.getItem("readerSize") as any) || 16
    ) as 14 | 16 | 18 | 20;

    this.state = {
      inputField: "",
      history: [],
      loadingNewChat: false,
      conversation_id: "",
      showBothVoiceText: false,
      plan: "Basic",
      popUp: false,
      reasonForPopUp: "monthly_limit",
      popUpReplace: "",
      newChatReceived: false,
      bookTalk: false,
      streaming: false,
      readerMode: false,
      showSettings: false,
      selectedFont: ["lexend", "opendyslexia"].includes(savedFont)
        ? savedFont
        : "lexend",
      selectedSize: [14, 16, 18, 20].includes(savedSize) ? savedSize : 16,
      currentBookmarkIndex: -1,
      isModalOpen: false,
      requiredTier: "",
      isImageFeature: false,
      imageNotice: "",
    };
  }

  scrollToBottom() {
    setTimeout(() => {
      if (this.chatHistoryRef.current) {
        this.chatHistoryRef.current.scrollTop =
          this.chatHistoryRef.current.scrollHeight;
      }
    }, 100); // Adjust the delay as needed
  }

  scrollDownByThirtyPercent() {
    setTimeout(() => {
      const el = this.chatHistoryRef.current;
      if (el) {
        // Compute 30% of the element’s visible height:
        const delta = el.clientHeight * 0.2;
        // Increase scrollTop by that amount (i.e. “scroll down” 30% of the container height)
        el.scrollTop = el.scrollTop + delta;
      }
    }, 100); // you can tweak or remove this delay if not needed
  }

  componentDidMount() {
    //this.scrollToBottom();

    if (this.props.bookTalk == "true") {
      this.setState({ bookTalk: true });
    }

    setTimeout(() => {
      this.restoreScrollPosition();
      this.setupScrollListener();
    }, 1000);

    emitter.on("toggleReaderMode", (bool: boolean) => {
      this.setState({ readerMode: bool });
      localStorage.setItem("toggleReaderMode", `${bool}`);
    });

    emitter.on("upgradePopup", (requiredTier: string) => {
      this.setState({
        requiredTier,
        isModalOpen: requiredTier.length > 0 ? true : false,
        isImageFeature: false,
        imageNotice: "",
      });
    });

    if (this.props.c && this.props.c.length > 5) {
      this.getCurrentConversationFromAPI();
    } else {
      const history = [
        {
          message: this.props.character.greeting,
          right: true,
        },
      ];
      this.setState({
        history,
      });
      emitter.emit("onHistoryUpdate", history);
    }

    // Check for and load voice preferences
    const voiceSettings = JSON.parse(
      localStorage.getItem("voice_settings") || "[]"
    );
    const userSetting = voiceSettings.find((setting: any) =>
      setting.id === this.props.id && setting.c === this.props.c
        ? this.props.c
        : this.state.conversation_id
    );

    if (userSetting) {
      this.setState({ showBothVoiceText: userSetting.showBothVoiceText });
    } else {
      // If not found, add to localStorage
      voiceSettings.push({
        id: this.props.id,
        showBothVoiceText: false,
        c: this.props.c ? this.props.c : this.state.conversation_id,
      });
      localStorage.setItem("voice_settings", JSON.stringify(voiceSettings));
    }
  }

  componentWillUnmount() {
    this.imageAbortController?.abort();
    this.removeScrollListener();
  }

  setupScrollListener() {
    if (this.chatHistoryRef.current) {
      this.chatHistoryRef.current.addEventListener("scroll", this.handleScroll);
    }
  }

  removeScrollListener() {
    if (this.chatHistoryRef.current) {
      this.chatHistoryRef.current.removeEventListener(
        "scroll",
        this.handleScroll
      );
    }
  }

  handleScroll = () => {
    // Throttle scroll events for performance
    if (!this.scrollTimeout) {
      this.scrollTimeout = setTimeout(() => {
        this.saveScrollPosition();
        this.scrollTimeout = null;
      }, 100);
    }
  };

  saveScrollPosition() {
    if (this.chatHistoryRef.current) {
      try {
        const scrollPosition = this.chatHistoryRef.current.scrollTop;
        const maxScroll =
          this.chatHistoryRef.current.scrollHeight -
          this.chatHistoryRef.current.clientHeight;
        const progress = scrollPosition / maxScroll;

        console.log(
          `bookmark_${
            this.props.c && this.props.c.length > 5
              ? this.props.c
              : this.state.conversation_id
          }`
        );

        localStorage.setItem(
          `bookmark_${
            this.props.c && this.props.c.length > 5
              ? this.props.c
              : this.state.conversation_id
          }`,
          JSON.stringify({
            progress,
            timestamp: Date.now(),
          })
        );
      } catch (error) {
        console.error(error);
      }
    }
  }

  restoreScrollPosition() {
    const bookmark = localStorage.getItem(
      `bookmark_${
        this.props.c && this.props.c.length > 5
          ? this.props.c
          : this.state.conversation_id
      }`
    );
    if (bookmark && this.chatHistoryRef.current) {
      const { progress } = JSON.parse(bookmark);
      console.log(progress);
      setTimeout(() => {
        if (this.chatHistoryRef.current) {
          const maxScroll =
            this.chatHistoryRef.current.scrollHeight -
            this.chatHistoryRef.current.clientHeight;
          this.chatHistoryRef.current.scrollTop = maxScroll * progress;
        }
      }, 500); // Wait for messages to render
    }
  }

  async componentDidUpdate(prevProps: ChatProps, prevState: ChatState) {
    if (prevState.conversation_id !== this.state.conversation_id) {
      this.restoreScrollPosition();
    }

    if (prevProps.c !== this.props.c) {
      await this.getCurrentConversationFromAPI();
      //this.scrollToBottom();
      this.setState({ newChatReceived: false });
      this.restoreScrollPosition();
    }

    if (this.props.newChat) {
      this.setState({
        history: [
          {
            message: this.props.character.greeting,
            right: true,
          },
        ],
        conversation_id: "",
      });

      this.props.resetChatFunc();
    }

    // shallow‐compare the arrays (or do a deep‐compare if you really need to)
    if (isEqual(prevState.history, this.state.history)) {
      emitter.emit("onHistoryUpdate", this.state.history);
    }
  }

  async getCurrentConversationFromAPI() {
    try {
      const response: any = await getConversationsByConversationId(
        this.props.c
      );
      const newArr: any = [];

      for (let i = 0; i < response.length; i++) {
        const message = response[i];
        if (message.role === "system" || message.role === "tool") continue;

        newArr.push({
          right: message.role === "assistant" ? true : false,
          message: message.content,
          message_type: message.message_type,
          media_url: message.media_url,
          media_alt: message.media_alt,
        });
      }
      this.setState({ history: newArr });
      //this.scrollToBottom();
    } catch (error) {}
  }

  private imageRequestInFlight = false;
  private imageAbortController?: AbortController;

  onGenerateImage = async (
    input: string,
    userReferenceImageData?: string,
    requestType: "selfie" | "nude" | "custom" = "custom"
  ) => {
    if (this.imageRequestInFlight || this.state.loadingNewChat) return;
    this.imageRequestInFlight = true;
    this.imageAbortController = new AbortController();
    const analyticsStart = performance.now();
    const analyticsContext = { membership_level: membershipLevel(this.props.userPlan) };
    if (requestType === "selfie") track("selfie_requested", analyticsContext);
    let noticeTimer: ReturnType<typeof setTimeout> | undefined;
    try {
      if (!this.state.newChatReceived) {
        this.setState({ newChatReceived: true });
      }
      this.scrollToBottom();

      const historyArr = [...this.state.history];
      historyArr.push({ message: input, right: false });
      this.setState({
        history: historyArr,
        loadingNewChat: true,
      });

      emitter.emit("onHistoryUpdate", historyArr);

      const imageData: ImageChatRequest = {
        prompt: input,
        conversation_id:
          this.props.c && this.props.c.length > 5
            ? this.props.c
            : this.state.conversation_id,
        user_reference_image_data: userReferenceImageData,
        image_request_type: requestType,
      };

      const response: any = await postImageForCharacter(
        this.props.id,
        this.props.bookTalk,
        imageData
      );
      let earlyImageShown = false;
      if (requestType === "nude" && response.job_id) {
        noticeTimer = setTimeout(() => {
          if (earlyImageShown || this.imageAbortController?.signal.aborted) return;
          historyArr.push({ right: true, message: "Give me two minutes to find one." });
          this.setState({ history: [...historyArr] });
          this.scrollToBottom();
        }, 7000);
      }
      const completed = response.job_id
        ? await waitForImageGenerationJob(response.job_id, (job: any) => {
            if (job.media_url && !earlyImageShown) {
              earlyImageShown = true;
              this.setState({
                history: [
                  ...historyArr,
                  {
                    right: true,
                    message: "",
                    message_type: "image",
                    media_url: job.media_url,
                    media_alt: "Generated image (saving...)"
                  }
                ]
              });
            }
          }, 900000, this.imageAbortController.signal)
        : response;
      const finalResponse = completed.result || completed;
      if (!finalResponse.media_url) throw new Error("No image was returned.");
      if (requestType === "selfie") track("selfie_created", {
        ...analyticsContext, elapsed_ms: performance.now() - analyticsStart,
        outcome: "success",
      });

      historyArr.push({
        right: true,
        message: finalResponse.message,
        message_type: finalResponse.message_type,
        media_url: finalResponse.media_url,
        media_alt: finalResponse.media_alt,
      });

      let updateObj: any = {
        history: historyArr,
        loadingNewChat: false,
        streaming: false,
      };

      if (finalResponse.conversation_id) {
        updateObj.conversation_id = finalResponse.conversation_id;
        const { updateConversationId } = this.props;
        updateConversationId(
          finalResponse.conversation_id.length > 5 ? finalResponse.conversation_id : ""
        );
      }

      this.setState(updateObj);
    } catch (error: any) {
      if (this.imageAbortController?.signal.aborted) return;
      if (requestType === "selfie") track("selfie_failed", { ...analyticsContext,
        reason: imageFailure(error), elapsed_ms: performance.now() - analyticsStart });
      if (error.monthly_limit) {
        this.setState({
          popUp: true,
          reasonForPopUp: "monthly_limit",
          loadingNewChat: false,
        });
        return;
      }

      if (error.monthly_limit_with_bonus) {
        this.setState({
          popUp: true,
          reasonForPopUp: "monthly_limit_with_bonus",
          loadingNewChat: false,
        });
        return;
      }

      if (error.plus_trail_ended) {
        this.setState({
          popUp: true,
          reasonForPopUp: "plus_trail_ended",
          loadingNewChat: false,
        });

        return;
      }

      if (error.exhausted_current_subscription) {
        this.setState({
          popUp: true,
          reasonForPopUp: "exhausted_current_subscription",
          loadingNewChat: false,
        });

        return;
      }

      if (error.premium_only) {
        this.setState({
          isModalOpen: true,
          requiredTier: "premium",
          isImageFeature: true,
          imageNotice:
            error.message ||
            "Image creation is available to Patron and Premium subscribers.",
          loadingNewChat: false,
        });

        return;
      }

      if (error.pg_only || error.image_limit) {
        this.setState({
          isModalOpen: true,
          requiredTier: "premium",
          isImageFeature: true,
          imageNotice: error.message,
          loadingNewChat: false,
        });

        return;
      }

      if (error.image_moderated) {
        this.setState({
          isModalOpen: true,
          requiredTier: "provider",
          isImageFeature: true,
          imageNotice:
            error.message ||
            "This image request was blocked by the image provider. Try a different pose or description.",
          loadingNewChat: false,
        });
        return;
      }

      this.setState({ loadingNewChat: false });

      if (error.upgrade_needed) return;

      // Keep image failures in the current conversation. Reloading here hid
      // provider/storage errors and made a failed request look like a normal
      // form submission to the reader.
      this.setState({
        isModalOpen: true,
        requiredTier: "provider",
        isImageFeature: true,
        imageNotice:
          error.message ||
          "The image request could not be completed. Please try again.",
      });
    } finally {
      if (noticeTimer) clearTimeout(noticeTimer);
      this.imageRequestInFlight = false;
      setTimeout(() => {
        const lastMessageAssistant: string = [...this.state.history]
          .reverse()[0]?.message;

        if (lastMessageAssistant && lastMessageAssistant.length > 800) return;
        this.scrollToBottom();
      }, 500);
    }
  };

  async conversationStartToGetLong(conversation_id: string) {
    await delay(3000);
    // Retrieve all records from localStorage
    const recordsString = localStorage.getItem("recentRecords") || "[]"; // Default to '[]' if null
    const records: any[] = JSON.parse(recordsString);

    function getRecentRecords(records: any[], thisX: any) {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      return records.filter((record: any) => {
        const recordDate = new Date(record.date);
        return (
          recordDate < sevenDaysAgo && // Changed this line to check for dates older than 7 days
          record.id === thisX.props.id &&
          record.c === thisX.props.c
        );
      });
    }

    let filtered = records.filter(
      (record: any) =>
        record.id === this.props.id && record.c === conversation_id
    );

    // Filter records from the last 7 days
    let recentRecords = getRecentRecords(records, this);

    if (filtered.length === 0) {
      records.push({
        date: new Date(),
        id: this.props.id,
        c: conversation_id,
        counter: 1,
      });
      localStorage.setItem("recentRecords", JSON.stringify(records));
      // this.setState({
      //   popUp: true,
      //   reasonForPopUp: "starting_long_conversation",
      // }); //  Let's just ingore the first notification, only when it happens two in a row
    } else {
      if (filtered[0].counter === 1) {
        const index = records.findIndex(
          (record) => record.id == this.props.id && record.c == conversation_id
        );

        records[index].counter = 2;
        localStorage.setItem("recentRecords", JSON.stringify(records));

        this.setState({
          popUp: true,
          reasonForPopUp: "starting_long_conversation",
        });
        return;
      }

      if (recentRecords.length === 0) return;

      let index = -1;

      records.map((record: any, index: number) => {
        if (
          record.id === recentRecords[0].id &&
          record.c === recentRecords[0].c
        ) {
          index = index;
        }
      });

      if (index > -1) {
        records[index].date = new Date();
        records[index].counter = records[index].counter++;
      }
      // Optionally update localStorage, if needed
      localStorage.setItem("recentRecords", JSON.stringify(records));
    }
    // Check if there are any recent records that meet the criteria
    if (recentRecords.length > 0) {
      this.setState({
        popUp: true,
        reasonForPopUp: "starting_long_conversation",
      });
    }
  }

  async delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  onInputChange = async (input: string) => {
    try {
      if (!this.state.newChatReceived) {
        this.setState({ newChatReceived: true });
      }
      this.scrollToBottom();

      // Copy the current history array
      const historyArr = [...this.state.history];

      // Add the user's message to the history
      historyArr.push({ message: input, right: false });
      this.setState({
        history: historyArr,
        loadingNewChat: true,
        streaming: true,
      });

      emitter.emit("onHistoryUpdate", historyArr);

      const conversation: any[] = [];
      historyArr.map((value) => {
        conversation.push({
          role: value.right ? "assistant" : "user",
          content: value.message,
        });
      });

      let payload: any = {
        conversation,
        conversation_id:
          this.props.c && this.props.c.length > 5
            ? this.props.c
            : this.state.conversation_id,
        plan: this.state.plan,
      };

      let response: any;
      let conversationIdResponse: string = "";

      // ─── Record the moment streaming “started” ───
      const streamingStartTime = Date.now();
      let hasScrolledOnThisResponse = false;

      historyArr.push({
        message: "",
        right: true,
      });
      this.setState({ history: historyArr });
      try {
        response = await postChatForCharacter(
          this.props.id,
          this.props.bookTalk,
          payload,
          async (streamPayload: any) => {
            if (streamPayload.message) {
              const lastElement = historyArr[historyArr.length - 1];

              lastElement.message += `${streamPayload.message}`;
              lastElement.message = lastElement.message.replace(/【.*?】/g, "");

              this.setState({ history: historyArr });

              // Check if at least 1 second has passed, and if we haven’t scrolled yet
              const now = Date.now();
              if (
                !hasScrolledOnThisResponse &&
                now - streamingStartTime >= 1000
              ) {
                this.scrollDownByThirtyPercent();
                hasScrolledOnThisResponse = true;
              }

              if (lastElement.message.length > 2) {
                this.setState({ loadingNewChat: false });
              }
            }

            if (streamPayload.longConversationWarning) {
              this.conversationStartToGetLong(streamPayload.conversation_id);
            }

            let updateObj: any = {
              inputField: input,
              history: historyArr,
            };

            if (streamPayload.conversation_id) {
              updateObj.conversation_id = streamPayload.conversation_id;
              conversationIdResponse = streamPayload.conversation_id;
              const { updateConversationId } = this.props;
              updateConversationId(
                streamPayload.conversation_id.length > 5
                  ? streamPayload.conversation_id
                  : ""
              );
            }

            // Update the state with the new history
            this.setState(updateObj);

            //this.scrollDownByThirtyPercent();
          },
          async (streamFinished: any) => {
            if (streamFinished) {
              this.setState({ loadingNewChat: false, streaming: false });
              await this.delay(3000);
              const lastMessageAssistant = [...this.state.history].reverse()[0]
                .right;
              if (!this.state.popUp && !lastMessageAssistant) {
                localStorage.setItem(
                  "streamError",
                  JSON.stringify(streamFinished)
                );
                reloadPage(
                  `${window.location.origin}/chat/${this.props.id}/c/${
                    this.props.c
                      ? this.props.c
                      : conversationIdResponse.length > 0
                      ? conversationIdResponse
                      : this.state.conversation_id
                  }`
                );
              }
            }
          }
        );
        return;
      } catch (error: any) {
        if (error.assistant_id_confilct) {
          let popUpReplace: string = "";
          error.plans_used_for_this_conversation
            .map((plan: string) => plan.charAt(0).toUpperCase() + plan.slice(1))
            .forEach((plan: string, index: number) => {
              popUpReplace += index === 0 ? plan : ` and ${plan}`;
            });

          this.setState({
            popUp: true,
            reasonForPopUp: "assistant_id_confilct",
            loadingNewChat: false,
            popUpReplace,
          });

          return;
        }

        if (error.monthly_limit) {
          this.setState({
            popUp: true,
            reasonForPopUp: "monthly_limit",
            loadingNewChat: false,
          });

          return;
        }

        if (error.monthly_limit_with_bonus) {
          this.setState({
            popUp: true,
            reasonForPopUp: "monthly_limit_with_bonus",
            loadingNewChat: false,
          });

          return;
        }

        if (error.plus_trail_ended) {
          this.setState({
            popUp: true,
            reasonForPopUp: "plus_trail_ended",
            loadingNewChat: false,
          });

          return;
        }

        if (error.exhausted_current_subscription) {
          this.setState({
            popUp: true,
            reasonForPopUp: "exhausted_current_subscription",
            loadingNewChat: false,
          });

          return;
        }

        this.setState({ loadingNewChat: false });

        if (error.upgrade_needed) return;

        localStorage.setItem("refreshErrorCatch", JSON.stringify(error));

        reloadPage(
          `${window.location.origin}/chat/${this.props.id}/c/${
            this.props.c ? this.props.c : this.state.conversation_id
          }`
        );
      } finally {
        setTimeout(() => {
          const lastMessageAssistant: string = [
            ...this.state.history,
          ].reverse()[0].message;

          if (lastMessageAssistant && lastMessageAssistant.length > 800) return;
          this.scrollToBottom();
        }, 500);
      }
    } catch (error) {
      this.setState({ loadingNewChat: false });
    }
  };

  updateShowBothVoiceText(showBothVoiceText: boolean) {
    this.setState({ showBothVoiceText });

    const voiceSettings = JSON.parse(
      localStorage.getItem("voice_settings") || "[]"
    );
    const settingIndex = voiceSettings.findIndex((setting: any) =>
      setting.id === this.props.id && setting.c === this.props.c
        ? this.props.c
        : this.state.conversation_id
    );

    if (settingIndex !== -1) {
      voiceSettings[settingIndex].showBothVoiceText = showBothVoiceText;
    } else {
      voiceSettings.push({
        id: this.props.id,
        showBothVoiceText,
        c: this.props.c ? this.props.c : this.state.conversation_id,
      });
    }

    localStorage.setItem("voice_settings", JSON.stringify(voiceSettings));
  }

  handleFontChange = (font: "lexend" | "opendyslexia") => {
    this.setState({ selectedFont: font });
    localStorage.setItem("readerFont", font); // Add this line
  };

  handleSizeChange = (size: 14 | 16 | 18 | 20) => {
    this.setState({ selectedSize: size });
    localStorage.setItem("readerSize", size.toString()); // Add this line
  };

  loadBookmark() {
    const bookmark = localStorage.getItem(
      `bookmark_${this.state.conversation_id}`
    );
    if (bookmark) {
      const index = parseInt(bookmark);
      this.scrollToMessage(index);
    }
  }

  scrollToMessage(index: number) {
    if (this.chatHistoryRef.current) {
      const element = this.chatHistoryRef.current.querySelector(
        `#message-${index}`
      );
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }

  handleJumpToBookmark = () => {
    const bookmark = localStorage.getItem(
      `bookmark_${this.state.conversation_id}`
    );
    if (bookmark) {
      const index = parseInt(bookmark);
      this.scrollToMessage(index);
    }
  };

  render() {
    return (
      <>
        <SettingsModal
          openSettings={this.state.showSettings}
          selectedFont={this.state.selectedFont}
          selectedSize={this.state.selectedSize}
          onFontChange={this.handleFontChange}
          onSizeChange={this.handleSizeChange}
          onClose={() => this.setState({ showSettings: false })}
        />

        {this.state.isModalOpen && this.renderModal()}

        {this.state.popUp && (
          <div className="fixed">
            <Popup
              open={this.state.popUp}
              reasonForPopUp={this.state.reasonForPopUp}
              setOpen={(bool: boolean) => this.setState({ popUp: bool })}
              replace={this.state.popUpReplace}
            ></Popup>
          </div>
        )}

        <div className="chat">
          <div className="hidden md:block absolute top-10 right-0">
            {this.props.userPlan === "Premium" && this.props.isVoiceEnabled && (
              <div className=" w-[20rem] bookish-input-group ml-auto">
                <div
                  className="bookish-on-off flex"
                  onClick={() =>
                    this.updateShowBothVoiceText(!this.state.showBothVoiceText)
                  }
                  style={{
                    width: "auto",
                    padding: ".2rem .3rem",
                    margin: ".3rem",
                  }}
                >
                  <p className="label mt-1">Always include text</p>
                  <div
                    className={`rectangle ml-auto ${
                      this.state.showBothVoiceText ? "rectangle-active" : ""
                    }`}
                  >
                    <div
                      className={`ellipse ${
                        this.state.showBothVoiceText ? "ellipse-active" : ""
                      }`}
                    ></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <p className="character-name md:mt-2">
              {this.props.character?.character_name}
            </p>
            {!this.state.readerMode && (
              <>
                {" "}
                <p
                  className="character-desc"
                  dangerouslySetInnerHTML={{
                    __html: this.props.character?.human_description,
                  }}
                ></p>
                <p
                  className="block lg:hidden cursor-pointer"
                  style={{ color: "#0070E0", textAlign: "center" }}
                  onClick={() => this.props.showHistory()}
                >
                  (See chat history )
                </p>
              </>
            )}

            {this.props.bookTalk && (
              <div className=" flex flex-col justify-center items-center">
                <div id="desktop-element-3" className="flex items-center gap-4">
                  <label className="text-lg font-medium text-gray-800">
                    Reader Mode
                  </label>
                  <button
                    onClick={() => {
                      const newState = !this.state.readerMode;
                      this.setState({ readerMode: newState });
                      emitter.emit("toggleReaderMode", newState);
                    }}
                    className={`w-14 h-8 flex items-center rounded-full p-1 transition-colors duration-300 ${
                      this.state.readerMode
                        ? "bg-[var(--primary-color)]"
                        : "bg-[var(--secondary-color)]"
                    }`}
                  >
                    <div
                      className={`bg-white w-6 h-6 rounded-full shadow-md transform transition-transform duration-300 ${
                        this.state.readerMode
                          ? "translate-x-6"
                          : "translate-x-0"
                      }`}
                    />
                  </button>

                  {this.state.readerMode && (
                    <button
                      onClick={() => this.setState({ showSettings: true })}
                      className="text-gray-700 hover:text-gray-900"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-center gap-3">
              <>
                {!this.props.saved ? (
                  <div
                    id="mobile-element-2"
                    className="flex lg:hidden  flex-col align-between cursor-pointer"
                    onClick={() => this.props.save()}
                  >
                    <img
                      src="/assets/images/heart.svg"
                      className="h-6"
                      alt=""
                    />
                  </div>
                ) : (
                  <div
                    className="flex  lg:hidden flex-col align-between cursor-pointer"
                    onClick={() => this.props.save()}
                  >
                    <img
                      src="/assets/images/heart-rose.svg"
                      alt=""
                      className="h-6"
                    />
                  </div>
                )}
              </>
              {this.props.userPlan === "Premium" && (
                <div
                  className="flex lg:hidden  flex-col align-between cursor-pointer"
                  onClick={() => this.props.updateIsVoiceEnabled()}
                >
                  {this.props.isVoiceEnabled ? (
                    <img
                      src="/assets/images/speaking.svg"
                      className="h-6"
                      alt=""
                    />
                  ) : (
                    <img
                      src="/assets/images/speak.svg"
                      className="h-6"
                      alt=""
                    />
                  )}
                </div>
              )}
            </div>
            {this.props.userPlan === "Premium" && this.props.isVoiceEnabled && (
              <div className="flex md:hidden w-[20rem] bookish-input-group mx-auto justify-center">
                <div
                  className="bookish-on-off flex"
                  onClick={() =>
                    this.updateShowBothVoiceText(!this.state.showBothVoiceText)
                  }
                  style={{
                    width: "15rem",
                    padding: ".2rem .3rem",
                    margin: ".3rem",
                  }}
                >
                  <p className="label mt-1" style={{ fontSize: "1rem" }}>
                    Always include text
                  </p>
                  <div
                    className={`rectangle ml-auto ${
                      this.state.showBothVoiceText ? "rectangle-active" : ""
                    }`}
                  >
                    <div
                      className={`ellipse ${
                        this.state.showBothVoiceText ? "ellipse-active" : ""
                      }`}
                    ></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mx-2 md:flex md:justify-center md:items-center">
            <div className="flex flex-col">
              <div
                className="flex-grow mt-8 mb-8 conversation-container"
                ref={this.chatHistoryRef}
                style={{ scrollBehavior: "smooth" }}
              >
                <ChatHistory
                  character={this.props.character}
                  history={this.state.history}
                  loading={this.state.loadingNewChat}
                  streaming={this.state.streaming}
                  isVoiceEnabled={this.props.isVoiceEnabled}
                  showBothVoiceText={this.state.showBothVoiceText}
                  userPlan={this.props.userPlan}
                  conversation_id={
                    this.props.c ? this.props.c : this.state.conversation_id
                  }
                  newChatReceived={this.state.newChatReceived}
                  readerMode={this.state.readerMode}
                  selectedFont={this.state.selectedFont}
                  selectedSize={this.state.selectedSize}
                />
              </div>
            </div>
          </div>

          <div
            className={`input-box-container ${
              this.props.action === "new" ? "h-full" : ""
            }`}
            style={{ display: this.state.popUp ? "none" : "block" }}
          >
            {this.props.action === "new" && (
              <PromptTabMobile
                sample_prompts={this.props.character.sample_prompts}
                book_talk_prompts={this.props.book_talk_prompts}
                userPlan={this.props.userPlan}
                bookTalk={this.props.bookTalk}
                updateParams={this.props.updateParams}
              />
            )}
            <div className="the-field-container mt-auto">
              <InputBox
                sample_prompts={this.props.character.sample_prompts}
                book_talk_prompts={this.props.book_talk_prompts}
                userPlan={this.props.userPlan}
                onInputChange={(input: any) => this.onInputChange(input)}
                onGenerateImage={(
                  input: string,
                  userReferenceImageData?: string,
                  requestType?: "selfie" | "nude" | "custom"
                ) =>
                  this.onGenerateImage(
                    input,
                    userReferenceImageData,
                    requestType
                  )
                }
                higherLevelProps={this.props}
                haveAnyPrompt={this.props.haveAnyPrompt}
              />
            </div>
          </div>
        </div>
      </>
    );
  }

  renderModal() {
    const { isModalOpen } = this.state;
    const isProviderError = this.state.requiredTier === "provider";

    if (!isModalOpen) return null;

    return (
      <div
        className="fixed inset-0 px-2 z-10 overflow-hidden flex items-center justify-center transition-opacity duration-300"
        onClick={() =>
          this.setState({
            isModalOpen: false,
            isImageFeature: false,
            imageNotice: "",
          })
        }
      >
        <div className="absolute inset-0 bg-gray-500 bg-opacity-75"></div>
        <div
          className="bg-white rounded-md shadow-xl overflow-hidden max-w-md w-full sm:w-96 md:w-1/2 lg:w-2/3 xl:w-1/3 z-50 transition-all duration-300 scale-100"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="text-white px-4 py-2 flex justify-between items-center bg-primary">
            <h2 className="text-lg font-semibold text-white">
              {isProviderError
                ? "Image request unavailable"
                : `Unlock ${this.state.requiredTier?.charAt(0).toUpperCase() +
                    this.state.requiredTier?.slice(1)} Features`}
            </h2>
          </div>
          {/* Modal Body */}
          <div className="p-4 text-center">
            {this.state.imageNotice ? (
              <p className="text-gray-800 text-lg mb-3">
                {this.state.imageNotice}
              </p>
            ) : this.state.isImageFeature ? (
              <p className="text-gray-800 text-lg mb-3">
                Image creation uses Grok Imagine Image 2.0. Premium subscribers
                have up to 50 images per month, and Patron subscribers receive up
                to 20 PG images per month.
              </p>
            ) : this.state.requiredTier === "patron" ? (
              <p className="text-gray-800 text-lg mb-3">
                Get access to exclusive short stories, works-in-progress,
                additional ChatPoints and more by upgrading to the Patron Plan.
              </p>
            ) : (
              <p className="text-gray-800 text-lg mb-3">
                Get access to additional characters, long conversations,
                full-length novels and more by upgrading to the Premium Plan.
              </p>
            )}

            {!isProviderError && (
              <Link to="/price" className="text-primary underline mt-4">
                Click here to upgrade now and enjoy exclusive benefits!
              </Link>
            )}
          </div>
          {/* Modal Footer */}
          <div className="flex justify-end border-t p-4">
            <div className="mt-2">
              {" "}
              <button
                onClick={() =>
                  this.setState({
                    isModalOpen: false,
                    isImageFeature: false,
                    imageNotice: "",
                  })
                }
                className="px-4 py-2 mr-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-md"
              >
                Close
              </button>
            </div>

            {!isProviderError && (
              <Link to="/price" className="text-primary underline mt-2">
                <button className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md font-semibold">
                  Upgrade Now
                </button>
              </Link>
            )}
          </div>
          {/* Close Button */}

          <button
            onClick={() =>
              this.setState({
                isModalOpen: false,
                isImageFeature: false,
                imageNotice: "",
              })
            }
            className="absolute -top-0 -right-0 bg-primary font-semibold hover:opacity-100 text-2xl w-10 h-10 rounded-full focus:outline-none text-white z-10"
          >
            ✗
          </button>
        </div>
      </div>
    );
  }
}

export default withRouter(Chat);
